import { useNavigation, useRoute } from '@react-navigation/native';
import { CustomFieldsModel, CustomFieldType, PrimaryKeyType, VisitsModel } from '@w3lcome/types';
import { Text, Button, CustomFieldInput, Header, GDPRModal, QRCodeButton } from '_/components';
import CheckVisitorIsForeigner from '_/components/CheckVisitorIsForeigner';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { GDPRButton } from '_/components/GDPRButton';
import KeyboardContainer from '_/components/KeyboardContainer';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

import { colors, fontSizes } from '_/config/theme';
import { useCustomization } from '_/hooks/CustomizationContext';
import { useVisit } from '_/hooks/VisitContext';
import { useNextCheckinScreen } from '_/hooks/useNextCheckinScreen';
import { CustomFieldWithError } from '_/interfaces/CustomFieldWithError';
import { VisitsModelCheckin } from '_/interfaces/VisitsModelCheckin';
import { AppRoute, CheckinProps } from '_/navigation/types';
import { smsOtpValidations } from '_/services/api';
import logger from '_/services/logger';
import useCountdownStore from '_/store/useCountdownStore';

import { parsePhoneNumber } from 'libphonenumber-js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, StyleSheet, TextInput, Keyboard, ScrollView } from 'react-native';
import { showMessage } from 'react-native-flash-message';

import formatFieldWhenVisitorIsForeigner from './helpers/formatFieldWhenVisitorIsForeigner';
import { useCentralizedValuesFallback } from '_/hooks/useCentralizedValuesFallback';
import { useChildrenCompanies } from '_/hooks/ChildrenCompaniesContex';
import { useDetailedVisitFlowLogging } from '_/hooks/useDetailedVisitFlowLogging';
import { checkVisitValidity } from '_/helpers/checkVisitValidity';
import { VisitorCheckinModals } from '_/components/VisitorCheckinModals';
import useVisitorCheckinStore from '_/components/VisitorCheckinModals/useVisitorCheckinStore';
import OCRCameraScreen from '_/components/OCRCameraScreen';
import SelectVisitModal from '_/components/SelectVisitModal';

const CheckinScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigation = useNavigation<CheckinProps>();
  const inputRef = useRef<TextInput>(null);
  const [isGDPRModalVisible, setGDPRModalVisible] = useState(true);

  const [isOCRCameraModalVisible, setOCRCameraModalVisible] = useState(false);
  const [activeVisits, setActiveVisits] = useState<null | VisitsModel[]>(null);

  const { customization, setSelectedVisitFlowType, company } = useCustomization();

  const route = useRoute();

  const meetingModalVisible = route.params;

  const {
    selectedOrFallbackCustomization,
    selectedOrFallbackGDPR,
    selectedOrFallbackVisitFlowType,
    selectedOrFallbackCompany,
    shouldSkipVisitFlowTypesScreen,
    selectedOrFallbackVisitFlowTypes,
  } = useCentralizedValuesFallback();

  const {
    selectedCompanyId,
    setSelectedCompanyId,
    setSelectedChildrenVisitFlowType,
    selectedChildrenVisitFlowType,
  } = useChildrenCompanies();

  const {
    loading,
    setVisit,
    getVisitorByPrimaryKey,
    getVisitDataByField,
    setVisitDataByField,
    isValidVisitDataByField,
    resetData,
    navigateToHome,
    visit,
    setVisitParser,
    defaultCountryCode,
  } = useVisit();

  const requireMeetingForCheckin =
    selectedOrFallbackCompany?.visitsConfig?.requireMeetingForCheckin;

  const searchAllFlowTypes = selectedOrFallbackCustomization?.searchAllFlowTypes;

  const { goToNextScreen } = useNextCheckinScreen(AppRoute.CHECKIN);

  const [setQRCodeReaderVisible] = useVisitorCheckinStore((state) => [
    state.setQRCodeReaderVisible,
  ]);

  const [counter, startCountdown] = useCountdownStore((state) => [
    state.counter,
    state.startCountdown,
  ]);

  const [isFocusedInput, setIsFocusedInput] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  const { getCheckInStartTime } = useDetailedVisitFlowLogging();

  const primaryKeyField = useMemo(() => {
    const primaryKey = selectedOrFallbackVisitFlowType?.fields?.[0];

    return formatFieldWhenVisitorIsForeigner(primaryKey as CustomFieldWithError, visit, t);
  }, [visit.isForeigner]);

  const isNextEnabled = isValidVisitDataByField(primaryKeyField);

  const [setModalCheckVisitorNameVisible, setVerifyPhoneNumberModal, setMeetingModalVisible] =
    useVisitorCheckinStore((state) => [
      state.setModalCheckVisitorNameVisible,
      state.setVerifyPhoneNumberModal,
      state.setMeetingModalVisible,
    ]);

  const returnRoute = () => {
    if (selectedChildrenVisitFlowType) setSelectedChildrenVisitFlowType(null);

    if (shouldSkipVisitFlowTypesScreen) {
      if (selectedCompanyId) setSelectedCompanyId(undefined);
      navigation.goBack();
    }
    resetData();
  };

  const onRejectGDPR = () => {
    navigateToHome(navigation);
  };

  const primaryKey: PrimaryKeyType = useMemo(() => {
    if (!selectedOrFallbackVisitFlowType?.primaryKey) {
      return PrimaryKeyType.EMAIL;
    }
    return selectedOrFallbackVisitFlowType.primaryKey;
  }, [selectedOrFallbackVisitFlowType]);

  const handleSubmit = async () => {
    const { visit: visitor, visits } = (await getVisitorByPrimaryKey()) ?? {};

    if (!visitor) {
      return;
    }

    if (visits?.length) {
      setActiveVisits(visits);
      return;
    }

    getVisitor(visitor);
  };

  async function getVisitor(visitor: Partial<VisitsModelCheckin>) {
    getCheckInStartTime(visitor);

    if (requireMeetingForCheckin && !visitor.meetingId) {
      return navigation.navigate(AppRoute.MEETING_ERROR_SCREEN);
    }

    if (
      !selectedOrFallbackCustomization?.ipadHideVisitorInfoEnable &&
      primaryKey === PrimaryKeyType.PHONE &&
      selectedOrFallbackVisitFlowType?.otpValidationEnable &&
      !visitor.meeting &&
      !visitor.smsOtpValidationId &&
      !visitor?.isCanceled
    ) {
      await requestOTPValidation(visit);
      setVerifyPhoneNumberModal(true);
      return;
    }

    if (
      selectedOrFallbackCustomization?.ipadHideVisitorInfoEnable &&
      visitor.name &&
      !visitor?.isCanceled &&
      !visitor.meeting
    ) {
      setVisit({ isRecurrent: true });
      setModalCheckVisitorNameVisible(true);
      return;
    }

    if (visitor.meeting) {
      Keyboard.dismiss();

      const isMeetingValid = checkVisitValidity(visitor, selectedOrFallbackCustomization);
      if (!isMeetingValid) {
        return navigation.navigate(AppRoute.CHECKIN_INFORMATION);
      }

      if (searchAllFlowTypes) {
        const visitFlowType = selectedOrFallbackVisitFlowTypes.find(
          (vft) => vft.id === visitor.visitFlowTypeId
        );

        if (!visitFlowType) return goToNextScreen();

        const isDifferentCompany = visitFlowType.companyId !== company?.id;
        const setVisitFlow = isDifferentCompany
          ? setSelectedChildrenVisitFlowType
          : setSelectedVisitFlowType;

        setVisitFlow(visitFlowType);

        if (visitFlowType?.serproValidationEnabled) {
          return visitFlowType?.serproCoHostValidationEnabled
            ? navigation.navigate(AppRoute.CHECKIN_COHOST_SELECT)
            : goToNextScreen(visitFlowType);
        }
      }

      setMeetingModalVisible(true);
    } else {
      if (!visitor?.isCanceled) {
        navigation.navigate(AppRoute.CHECKIN_INFORMATION);
      }
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!selectedOrFallbackCustomization?.gdprEnabled) {
        inputRef.current?.focus();
      }
    }, 800);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const anonymousCheckIn = () => {
    setVisit({ isAnonymous: true });
    setGDPRModalVisible(false);
    goToNextScreen();
  };

  const requestOTPValidation = useCallback(
    async (visit: Partial<VisitsModelCheckin>) => {
      if (!visit.phone) {
        return;
      }

      if (visit.phone === phoneNumber && counter !== 0) {
        return;
      }

      try {
        await (selectedCompanyId
          ? smsOtpValidations.childrenRequestOtp(selectedCompanyId, {
              phone: parsePhoneNumber(visit.phone, visit.countryCode).number,
            })
          : smsOtpValidations.insert({
              phone: parsePhoneNumber(visit.phone, visit.countryCode).number,
            }));

        setPhoneNumber(visit.phone);
        startCountdown(selectedOrFallbackVisitFlowType?.otpValidationTimeToExpireInMinutes || 2);
      } catch (error) {
        logger(error);
      }
    },
    [counter, phoneNumber]
  );

  const handleNextScreen = () => {
    if (!isNextEnabled) {
      showMessage({
        message: t('infoScreen.missingFieldsError', { defaultValue: 'Alguns campos não foram preenchidos corretamente' }),
        backgroundColor: colors.danger,
      });
      return;
    }

    if (selectedOrFallbackCustomization?.gdprEnabled && selectedOrFallbackGDPR?.gdprText) {
      if (visit.gdprAgreedAt) {
        handleSubmit();
      } else {
        setGDPRModalVisible(true);
      }
    } else {
      handleSubmit();
    }
  };

  function handleAcceptGDPR() {
    setVisit({ gdprAgreedAt: new Date() });
    setGDPRModalVisible(false);
    setImmediate(() => {
      inputRef.current?.focus();
    });
  }

  function handleOpenGDPR() {
    Keyboard.dismiss();
    setGDPRModalVisible(true);
  }

  const keyboardType = useMemo(() => {
    switch (primaryKeyField?.type) {
      case CustomFieldType.PHONE:
      case CustomFieldType.CPF:
        return 'numeric';
    }
    return 'default';
  }, [primaryKeyField?.type]);

  const documentTypeField = selectedOrFallbackVisitFlowType?.fields?.find(
    (field: CustomFieldsModel) =>
      field.type.toUpperCase() === CustomFieldType.CPF || field.type === CustomFieldType.RG
  );

  return (
    <>
      <KeyboardContainer
        keyboardOpen={isFocusedInput}
        onSubmit={handleNextScreen}
        inputName={primaryKey.toLocaleLowerCase()}
        onChange={(value) => setVisitDataByField(value, primaryKeyField)}
        keyboardType={keyboardType}
      >
        <ScrollView
          scrollEnabled={isGDPRModalVisible}
          style={[styles.container, StyleSheet.absoluteFill]}
          keyboardShouldPersistTaps="handled"
        >
          <View style={[styles.container, { alignItems: 'center' }]}>
            <ContainerWidthLimit>
              <Header onGoBack={returnRoute} />

              <View style={styles.content}>
                <Text center size={fontSizes.xl}>
                  {t(`homeScreen.message.${primaryKey}`, {
                    defaultValue: `Digite suas informações para continuar seu check-in ${primaryKey}`,
                  })}
                </Text>

                {documentTypeField && documentTypeField.id === 'primaryKey' && (
                  <View style={styles.foreignerOptionContainer}>
                    <CheckVisitorIsForeigner
                      foreignerDocumentSelected={visit.isForeigner ?? false}
                      onClick={() => setVisit({ isForeigner: !visit.isForeigner })}
                      buttonKey={documentTypeField.type}
                    />
                  </View>
                )}

                <CustomFieldInput
                  editable={!loading}
                  value={getVisitDataByField(primaryKeyField) as string}
                  onChangeText={(value) => setVisitDataByField(value, primaryKeyField)}
                  customField={primaryKeyField}
                  ref={inputRef}
                  countryCode={visit.countryCode ?? defaultCountryCode}
                  onSubmitEditing={handleSubmit}
                  onFocus={() => setIsFocusedInput(true)}
                  onBlur={() => setIsFocusedInput(false)}
                  onSubmit={handleNextScreen}
                  setCountryCode={(code) => setVisit({ countryCode: code })}
                />

                {selectedOrFallbackCustomization?.documentOcrEnabled &&
                  documentTypeField?.id === 'primaryKey' && (
                    <View style={{ width: '50%', marginBottom: 8, marginTop: 32 }}>
                      <Button
                        outline
                        onPress={() => setOCRCameraModalVisible(true)}
                        disabled={loading}
                        buttonStyles={{
                          borderRadius: 8,
                        }}
                        leftIcon={
                          <MaterialCommunityIcons
                            name="line-scan"
                            size={32}
                            style={{ color: colors.dark3, marginRight: 16 }}
                          />
                        }
                      >
                        <Text fontType="medium" size={18} color={colors.dark3}>
                          {t('Escanear documento')}
                        </Text>
                      </Button>
                    </View>
                  )}

                <Button
                  backgroundColor={customization?.mainColor || colors.primary}
                  labelColor={customization?.backgroundColor || colors.white}
                  loading={loading}
                  containerStyles={styles.buttonContainer}
                  buttonStyles={[
                    styles.button,
                    { justifyContent: loading ? 'center' : 'space-between' },
                  ]}
                  rightIcon="arrow-right"
                  onPress={handleNextScreen}
                >
                  {t('Next', {defaultValue: 'Próximo'})}
                </Button>
              </View>
            </ContainerWidthLimit>

            {selectedOrFallbackCustomization?.gdprEnabled && !!selectedOrFallbackGDPR?.gdprText && (
              <GDPRButton onOpen={handleOpenGDPR} />
            )}

            <View style={{ paddingVertical: 16, width: '50%' }}>
              <View
                style={{
                  marginVertical: 16,
                  backgroundColor: colors.light2,
                  height: 1,
                  width: '100%',
                }}
              />

              {selectedOrFallbackCustomization?.qrCodeCheckinEnabled &&
                !selectedOrFallbackCustomization.qrCodeCheckinAtHomeEnabled && (
                  <QRCodeButton
                    onPress={() => {
                      setQRCodeReaderVisible(true);
                    }}
                  />
                )}
            </View>
          </View>
        </ScrollView>
      </KeyboardContainer>

      {isOCRCameraModalVisible && (
        <OCRCameraScreen
          isVisible={isOCRCameraModalVisible}
          onClose={() => setOCRCameraModalVisible(false)}
          onScan={(data) => setVisit({ ...data, ocrUsed: true })}
          documentPrimaryKeyType={primaryKey}
          saveOcrPicture={!!selectedOrFallbackCustomization?.documentOcrSaveImageEnabled}
        />
      )}

      {selectedOrFallbackCustomization?.gdprEnabled && !!selectedOrFallbackGDPR?.gdprText && (
        <GDPRModal
          isVisible={isGDPRModalVisible}
          onReject={
            selectedOrFallbackCustomization?.gdprAnonymouslyCheckin
              ? anonymousCheckIn
              : onRejectGDPR
          }
          onAccept={handleAcceptGDPR}
        />
      )}

      {activeVisits && activeVisits?.length > 0 && (
        <SelectVisitModal
          isModalVisible={activeVisits?.length > 0}
          handleConfirm={(v) => {
            setVisitParser(v);
            getVisitor(v);
          }}
          visits={activeVisits}
          hideModal={() => {
            setActiveVisits(null);
            resetData();
          }}
        />
      )}

      <VisitorCheckinModals
        meetingModalVisible={!!meetingModalVisible}
        goToNextScreen={goToNextScreen}
      />
    </>
  );
};

export default CheckinScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  content: {
    alignItems: 'center',
  },
  buttonContainer: {
    width: '60%',
  },
  button: {
    marginTop: 26,
    marginBottom: 22,
    height: 64,
  },
  foreignerOptionContainer: {
    paddingBottom: 8,
  },
});
